'use client';

import { Card } from '@/components/cms/Card';
import { CMSSection } from '@/components/cms/CmsSection';
import { useSliderEntryEffect } from '@/hooks/useSliderEntryEffect';
import type { CMSContentItem } from '@/lib/cms/types';
import type {
  ContentfulCardFragment,
  ContentfulFragment,
  ContentGroupFragment,
} from '@/lib/contentful/types';
import { filterCollection } from '@/lib/contentful/utils';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import styles from './SliderMaxFourCards.module.scss';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/scrollbar';

export interface SliderMaxFourCardsProps {
  /** Contentful ContentGroup data — accepts both adapter format (flat items) and raw Contentful format (itemsCollection) */
  data: ContentGroupFragment & { items?: CMSContentItem[] };
  /** Optional extra data for cards */
  extraData?: Record<string, unknown>;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

/**
 * SliderMaxFourCards - Card carousel with up to 4 items visible
 *
 * A Swiper-based slider that displays Card items in a horizontal carousel.
 * Responsive breakpoints adjust the number of visible slides.
 *
 * Features:
 * - Navigation arrows (hidden when disabled)
 * - Scrollbar on desktop
 * - Responsive slides per view
 * - Smooth transitions
 *
 * @example
 * <SliderMaxFourCards
 *   data={contentGroupData}
 *   cmsIndex={0}
 * />
 */
export function SliderMaxFourCards({ data, cmsIndex, className }: SliderMaxFourCardsProps) {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  // Accept both adapter format (flat items) and raw Contentful format (itemsCollection)
  const rawCollection = data.items ? { items: data.items } : data.itemsCollection;
  const items = filterCollection<ContentfulFragment | CMSContentItem>(
    rawCollection as { items: (CMSContentItem | null | undefined)[] }
  );

  const effectIndices = useMemo(() => {
    const indices = new Set<number>();
    items.forEach((item, i) => {
      if ((item as { entryEffect?: boolean }).entryEffect) indices.add(i);
    });
    return indices;
  }, [items]);

  const { containerRef, isRevealed } = useSliderEntryEffect({
    swiper: swiperInstance,
    totalItems: items.length,
    effectIndices,
  });

  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `slidermaxfourcards-${cmsIndex}-${element}`
        : `slidermaxfourcards-${cmsIndex}`
      : undefined;

  if (items.length === 0) {
    return null;
  }

  // Responsive breakpoints for slides per view
  const breakpoints = {
    0: {
      slidesPerView: 1.2,
      spaceBetween: 12,
    },
    640: {
      slidesPerView: 1.3,
      spaceBetween: 16,
    },
    768: {
      slidesPerView: 2,
      spaceBetween: 16,
    },
    1024: {
      slidesPerView: 2,
      spaceBetween: 24,
    },
  };

  return (
    <CMSSection className={clsx(styles.section, className)} data-testid={testId()}>
      <div ref={containerRef as React.RefObject<HTMLDivElement>} className={styles.container}>
        <Swiper
          onSwiper={setSwiperInstance}
          modules={[Navigation, Scrollbar]}
          breakpoints={breakpoints}
          navigation
          scrollbar={{
            draggable: true,
          }}
          className={styles.swiper}
        >
          {items.map((item, index) => {
            const cardData = item as ContentfulCardFragment;
            const sysId = cardData?.sys?.id;
            const hasEffect = effectIndices.has(index);
            const revealed = isRevealed(index);

            const card = <Card type={cardData.type as never} data={cardData} cmsIndex={index} />;

            return (
              <SwiperSlide
                key={sysId || index}
                className={styles.slide}
                data-testid={testId(`slide-${index}`)}
              >
                {hasEffect ? (
                  <div
                    className={clsx(
                      styles.slideFill,
                      styles.entryEffect,
                      revealed && styles.visible
                    )}
                  >
                    {card}
                  </div>
                ) : (
                  card
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </CMSSection>
  );
}

SliderMaxFourCards.displayName = 'SliderMaxFourCards';
